// src/pages/Home.tsx
//
// Church-style GIMLP home page.
// Inspired by EventLandingPage:
//   • Full-screen dark-green hero with banner image + overlay + scripture badge
//   • Masonry gallery section (dark bg) with lightbox
//   • Circular leader portrait grid (dark navy bg)
//   • Verse / concept section with topic chips
//   • Warm pastoral testimony section
//   • No dashboard widgets, no stat cards — images and scripture lead
//
// Pure Tailwind CSS throughout. No custom fontFamily declarations.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Users,
  Shield,
  Globe,
  ArrowRight,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
   IMAGE PATHS  (swap for real photos)
───────────────────────────────────────────────────────────────── */

const BANNER = "/images/co.jpg";
const PHOTO = "/images/co.jpg";
const CYPPHOTO = "/images/cyp.webp";
const CHAIRPHOTO = "/images/chair2.jpeg";

/* ─────────────────────────────────────────────────────────────────
   SECTION TITLE  (matches EventLandingPage)
───────────────────────────────────────────────────────────────── */

const SectionTitle: React.FC<{
  text: string;
  light?: boolean;
  sub?: string;
}> = ({ text, light, sub }) => (
  <div className="text-center mb-12">
    <h2
      className={`text-2xl sm:text-3xl md:text-4xl font-black tracking-wide uppercase mb-4 ${light ? "text-white" : "text-gray-900"}`}
    >
      {text}
    </h2>
    <div className="w-14 h-1 bg-green-600 mx-auto rounded-full mb-4" />
    {sub && (
      <p
        className={`text-base md:text-lg leading-relaxed max-w-2xl mx-auto ${light ? "text-white/60" : "text-gray-500"}`}
      >
        {sub}
      </p>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   GALLERY GRID  (masonry + lightbox — same as EventLandingPage)
───────────────────────────────────────────────────────────────── */

// const GALLERY_IMAGES = Array(9).fill(PHOTO);

// const GalleryGrid: React.FC<{ images: string[] }> = ({ images }) => {
//   const [sel, setSel] = useState<string | null>(null);
//   return (
//     <>
//       <div className="columns-2 sm:columns-3 md:columns-4 gap-2">
//         {images.map((src, i) => (
//           <div
//             key={i}
//             onClick={() => setSel(src)}
//             className="break-inside-avoid mb-2 overflow-hidden rounded-md cursor-pointer hover:opacity-80 transition-opacity duration-200"
//           >
//             <img
//               src={src}
//               alt=""
//               className="w-full block object-cover"
//               loading="lazy"
//             />
//           </div>
//         ))}
//       </div>
//       {sel && (
//         <div
//           onClick={() => setSel(null)}
//           className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-6"
//         >
//           <img
//             src={sel}
//             alt=""
//             className="max-w-[88vw] max-h-[88vh] object-contain rounded-lg"
//             onClick={(e) => e.stopPropagation()}
//           />
//         </div>
//       )}
//     </>
//   );
// };

/* ─────────────────────────────────────────────────────────────────
   LEADER CARD  (circular portrait — matches SpeakerCard style)
───────────────────────────────────────────────────────────────── */

interface Leader {
  name: string;
  title: string;
  photoUrl?: string;
}

const LeaderCard: React.FC<{ leader: Leader }> = ({ leader }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="flex flex-col items-center text-center max-w-[180px]">
      <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full overflow-hidden border-4 border-green-600 shadow-[0_0_0_4px_rgba(255,255,255,0.06),0_8px_32px_rgba(0,0,0,0.45)] mb-4 shrink-0">
        <img
          src={err || !leader.photoUrl ? PHOTO : leader.photoUrl}
          alt={leader.name}
          onError={() => setErr(true)}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p className="font-bold text-sm sm:text-base text-white mb-1 leading-snug">
        {leader.name}
      </p>
      <p className="text-[11px] text-white/50 italic leading-snug">
        {leader.title}
      </p>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────────── */

const LEADERS: Leader[] = [
  {
    name: "Pastor E.A Odeyemi",
    title: "Continental Overseer",
    photoUrl: PHOTO,
  },
  {
    name: "Pastor Emmanuel Bakare",
    title: "Continental Youth Pastor",
    photoUrl: CYPPHOTO,
  },
  {
    name: "Pastor Godwin Maga",
    title: "Programme Director",
    photoUrl: CHAIRPHOTO,
  },
];

const PILLARS = [
  {
    icon: BookOpen,
    title: "Structured Learning",
    verse: "Proverbs 4:7",
    desc: "Wisdom is the principal thing — therefore get wisdom. A quarterly curriculum built around biblical and practical leadership principles.",
  },
  {
    icon: Users,
    title: "Community & Accountability",
    verse: "Proverbs 27:17",
    desc: "As iron sharpens iron, so one person sharpens another. Grow alongside a community of leaders who challenge and support your development.",
  },
  {
    icon: Shield,
    title: "Character Formation",
    verse: "Daniel 1:8",
    desc: "Daniel purposed in his heart. The programme develops leaders of integrity — spiritually grounded, professionally capable, and kingdom-minded.",
  },
  {
    icon: Globe,
    title: "Kingdom Impact",
    verse: "Isaiah 61:1",
    desc: "Anointed to preach good news. Every track is designed to equip leaders who carry influence into the church, workplace, and society.",
  },
];

// const TESTIMONIALS = [
//   {
//     name: "Adaeze N.",
//     role: "Programme Member · Q2 Cohort",
//     content:
//       "The structure helped me move from passive learning to deliberate growth. Every quarter gives me clarity on what to focus on next.",
//     avatar: "AN",
//   },
//   {
//     name: "Daniel O.",
//     role: "Youth Leader · Inner Circle",
//     content:
//       "The tasks, content, and feedback system gave me a practical way to measure my growth instead of just feeling inspired for a moment.",
//     avatar: "DO",
//   },
//   {
//     name: "Chiamaka E.",
//     role: "Regional Coordinator · Legacy Fellows",
//     content:
//       "What stands out most is the depth and intentionality. It is not just a portal — it is a system for formation and leadership development.",
//     avatar: "CE",
//   },
// ];

const TOPICS = [
  "Visionary Leadership",
  "Systems & Structures",
  "Spiritual Formation",
  "Cultural Legacy",
  "Kingdom Stewardship",
  "Team Building",
];

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const Home: React.FC = () => (
  <div className="min-h-screen bg-white">
    {/* ── HERO ── */}
    <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 md:px-10 py-24 overflow-hidden">
      {/* Full-bleed background image */}
      <img
        src={BANNER}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* Dark green overlay — matches EventLandingPage hero */}
      <div className="absolute inset-0 bg-gradient-to-b from-green-950/80 via-green-950/85 to-green-950/95" />

      {/* Decorative rings */}
      <div className="absolute -right-[10%] -top-[10%] w-[50vw] h-[50vw] rounded-full border border-white/5 pointer-events-none" />
      <div className="absolute -right-[5%]  -top-[5%]  w-[35vw] h-[35vw] rounded-full border border-white/[0.04] pointer-events-none" />
      <div className="absolute -left-[10%] bottom-[10%] w-[40vw] h-[40vw] rounded-full border border-white/[0.04] pointer-events-none" />

      <div className="relative z-10 max-w-4xl w-full flex flex-col items-center gap-6">
        {/* Scripture badge */}
        <div className="inline-flex items-center gap-2 border border-green-500/60 rounded-full px-5 py-2 text-green-400 text-[11px] font-black tracking-[0.22em] uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Proverbs 4:7 — Get wisdom, get understanding
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight drop-shadow-2xl">
          GIMLP Mentorship
          <br className="hidden sm:block" /> Programme
        </h1>

        {/* Tagline */}
        <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
          Generational Impact & Mentorship Legacy Program (GIMLP) is a structured quarterly journey equipping leaders in the church,
          workplace, and society with wisdom, accountability, and kingdom
          purpose.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-2">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 font-black uppercase tracking-widest bg-green-600 hover:bg-green-500 active:scale-95 transition-all text-white rounded shadow-lg shadow-green-900/40 text-sm px-10 py-4"
          >
            Join the Programme <ChevronRight size={15} />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 border border-white/25 rounded px-8 py-4 text-sm font-semibold text-white/80 hover:text-white hover:border-white/50 transition-all uppercase tracking-widest"
          >
            Sign In
          </Link>
        </div>

        {/* Scroll cue */}
        <ChevronDown className="text-white/25 mt-6 animate-bounce" size={22} />
      </div>
    </section>

    {/* ── ABOUT / MISSION ── */}
    <section id="about" className="bg-white py-20 md:py-24 px-4 md:px-10">
      <div className="max-w-5xl mx-auto">
        <SectionTitle
          text="Our Mission"
          sub="The GIMLP Mentorship Programme exists to raise leaders who think clearly, serve faithfully, and build lasting impact in every sphere of life."
        />

        {/* Split layout: image left, text right */}
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center mt-4">
          <div className="relative">
            <img
              src={PHOTO}
              alt="Programme community"
              className="w-full rounded-2xl object-cover aspect-[4/3]"
            />
            {/* Floating verse card */}
            <div className="absolute -bottom-5 -right-3 md:-right-6 bg-green-900 text-white rounded-2xl px-5 py-4 shadow-2xl max-w-[200px]">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-green-400 mb-1">
                Core Verse
              </p>
              <p className="text-sm font-bold leading-snug">
                "Wisdom is the principal thing."
              </p>
              <p className="text-[10px] text-white/50 mt-1 tracking-wider uppercase">
                Proverbs 4:7
              </p>
            </div>
          </div>

          <div className="pt-6 md:pt-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-green-600 mb-4">
              Who we are
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-gray-900 leading-snug mb-5">
              Formation over information.
              <br />
              Growth with direction.
            </h3>
            <p className="text-gray-600 leading-relaxed mb-5">
              GIMLP is a kingdom-focused leadership development programme
              operated within RCCG Continent 2. Through a structured quarterly
              journey, members engage with mentorship content, leadership tasks,
              peer accountability, and a community of like-minded leaders.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              We believe that leaders are not merely born — they are formed
              through discipline, community, and the pursuit of wisdom. Every
              quarter is a deliberate step toward character, capacity, and
              kingdom impact.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                "Quarter-based journey",
                "Mentorship tasks",
                "Content library",
                "Progress tracking",
              ].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full border border-green-200 text-green-700 text-xs font-bold tracking-wide uppercase"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── GALLERY (dark bg — matches EventLandingPage) ── */}
    {/* <section id="gallery" className="bg-green-950 py-20 md:py-24 px-4 md:px-10">
      <div className="max-w-6xl mx-auto">
        <SectionTitle
          text="From Our Community"
          light
          sub="Moments from gatherings, events, and the journey of leaders across the programme."
        />
        <GalleryGrid images={GALLERY_IMAGES} />
      </div>
    </section> */}

    {/* ── LEADERSHIP / LEADERS (dark green — matches Speakers section) ── */}
    <section id="leaders" className="bg-green-900 py-20 md:py-24 px-4 md:px-10">
      <div className="max-w-6xl mx-auto">
        <SectionTitle
          text="Our Leadership"
          light
          sub="The programme is guided by anointed and experienced leaders committed to the growth of every member."
        />
        <div className="flex flex-wrap justify-center gap-10 md:gap-16">
          {LEADERS.map((leader, i) => (
            <LeaderCard key={i} leader={leader} />
          ))}
        </div>
      </div>
    </section>

    {/* ── PROGRAMME PILLARS (white) ── */}
    <section id="pillars" className="bg-white py-20 md:py-24 px-4 md:px-10">
      <div className="max-w-6xl mx-auto">
        <SectionTitle
          text="Programme Pillars"
          sub="Four foundations that shape every quarter of the GIMLP journey."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => (
            <div
              key={i}
              className="group rounded-2xl border border-gray-100 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4 group-hover:bg-green-600 transition-colors">
                <p.icon
                  className="text-green-600 group-hover:text-white transition-colors"
                  size={22}
                />
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-green-600 mb-2">
                {p.verse}
              </p>
              <h3 className="text-lg font-black text-gray-900 mb-3 leading-snug">
                {p.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CONCEPT / VISION (dark gradient — matches EventLandingPage concept section) ── */}
    <section className="bg-gradient-to-br from-green-950 to-green-900 py-20 md:py-24 px-4 md:px-10">
      <div className="max-w-2xl mx-auto text-center">
        <SectionTitle text="What We Develop" light />

        <p className="text-base md:text-lg text-white/65 leading-relaxed mb-10">
          The programme intentionally develops leaders across multiple
          dimensions — spiritually, institutionally, and in their capacity to
          serve their generation with wisdom and lasting impact.
        </p>

        {/* Topic chips */}
        <div className="flex flex-wrap justify-center gap-2.5 mb-12">
          {TOPICS.map((t, i) => (
            <span
              key={i}
              className="px-4 py-1.5 rounded-full border border-green-500/40 text-green-400 text-xs font-bold tracking-widest uppercase"
            >
              {t}
            </span>
          ))}
        </div>

        {/* Central image with overlay */}
        <div className="relative rounded-2xl overflow-hidden mb-12 max-w-lg mx-auto">
          <img
            src={PHOTO}
            alt="Programme"
            className="w-full object-cover aspect-video opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-green-950/80 to-transparent flex items-end justify-start p-6">
            <div className="text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-green-400 mb-1">
                Core Scripture
              </p>
              <p className="text-white font-bold text-base md:text-lg leading-snug">
                "Get wisdom. Though it cost all you have, get understanding."
              </p>
              <p className="text-white/50 text-xs mt-1 uppercase tracking-wider">
                Proverbs 4:7 (NIV)
              </p>
            </div>
          </div>
        </div>

        <Link
          to="/register"
          className="inline-flex items-center gap-2 font-black uppercase tracking-widest bg-green-600 hover:bg-green-500 active:scale-95 transition-all text-white rounded shadow-lg shadow-green-900/40 text-sm px-10 py-4"
        >
          Join the Programme <ChevronRight size={15} />
        </Link>
      </div>
    </section>

    {/* ── FINAL CTA (matches EventLandingPage concept section tone) ── */}
    <section className="px-4 py-12 md:px-10 pb-24 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden">
          {/* Background image + overlay */}
          <img
            src={BANNER}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-950/90 to-green-900/90" />

          <div className="relative z-10 p-10 md:p-16 text-center">
            {/* Scripture badge */}
            <div className="inline-flex items-center gap-2 border border-green-500/60 rounded-full px-5 py-2 text-green-400 text-[11px] font-black tracking-[0.22em] uppercase mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Your next season starts here
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-5 max-w-3xl mx-auto">
              Step into a more intentional path of growth and leadership
            </h2>

            <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Join the GIMLP Mentorship Programme and begin a guided quarterly
              journey with clarity, accountability, and meaningful progress
              rooted in kingdom purpose.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 font-black uppercase tracking-widest bg-green-600 hover:bg-green-500 active:scale-95 transition-all text-white rounded shadow-lg shadow-green-900/40 text-sm px-10 py-4"
              >
                Create Your Account <ArrowRight size={15} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 border border-white/25 rounded px-8 py-4 text-sm font-semibold text-white/80 hover:text-white hover:border-white/50 transition-all uppercase tracking-widest"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
